import { parseSupplier, SUPPLIER_TYPES } from '@felix/domain';
import {
  Button,
  Checkbox,
  Modal,
  MultiSelect,
  NumberInput,
  Select,
  Textarea,
  TextInput,
} from '@mantine/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { errorCode } from '../personnel/source.js';
import classes from './Purchasing.module.css';
import { useRegionOptions } from './RfqMarket.js';
import type { SupplierSource } from './source.js';
import type { Supplier, SupplierInput } from './types.js';

type Values = Record<
  | 'name'
  | 'supplierType'
  | 'country'
  | 'city'
  | 'address'
  | 'contactPerson'
  | 'email'
  | 'phone'
  | 'website'
  | 'paymentTerms'
  | 'currency'
  | 'bankInfo'
  | 'taxInfo'
  | 'remarks',
  string
> & {
  approved: boolean;
  preferred: boolean;
  emergency: boolean;
  active: boolean;
  rating: number | '';
  /** Hizmet bölgeleri (ADR 0113 §3): yayın penceresindeki bölge süzgeci. */
  serviceRegions: string[];
};

const EMPTY: Values = {
  name: '',
  supplierType: '',
  country: '',
  city: '',
  address: '',
  contactPerson: '',
  email: '',
  phone: '',
  website: '',
  paymentTerms: '',
  currency: '',
  bankInfo: '',
  taxInfo: '',
  remarks: '',
  approved: false,
  preferred: false,
  emergency: false,
  active: true,
  rating: '',
  serviceRegions: [],
};

function valuesOf(supplier: Supplier): Values {
  return {
    name: supplier.name,
    supplierType: supplier.supplierType,
    country: supplier.country ?? '',
    city: supplier.city ?? '',
    address: supplier.address ?? '',
    contactPerson: supplier.contactPerson ?? '',
    email: supplier.email ?? '',
    phone: supplier.phone ?? '',
    website: supplier.website ?? '',
    paymentTerms: supplier.paymentTerms ?? '',
    currency: supplier.currency ?? '',
    bankInfo: supplier.bankInfo ?? '',
    taxInfo: supplier.taxInfo ?? '',
    remarks: supplier.remarks ?? '',
    approved: supplier.approved,
    preferred: supplier.preferred,
    emergency: supplier.emergency,
    active: supplier.active,
    rating: supplier.rating ?? '',
    serviceRegions: supplier.serviceRegions ?? [],
  };
}

function inputOf(values: Values): Record<string, unknown> {
  return { ...values, rating: values.rating === '' ? null : values.rating };
}

/**
 * Tedarikçi kartı (`satin-alma-modulu.md` §4): ad, tür, iletişim, onaylı / tercihli / acil,
 * ödeme koşulları, para birimi, banka ve vergi bilgisi, performans puanı. Kod sistemden
 * (sorular #79); tedarikçi silinmez, pasifleştirilir.
 */
export function SupplierDialog({
  opened,
  supplier,
  source,
  onClose,
  onSaved,
  defaults,
}: {
  opened: boolean;
  /** Düzeltilen; verilmezse yeni. */
  supplier: Supplier | null;
  /** Yeni kartın başlangıç değerleri (ör. RFQ'dan açılan kayıtsız tedarikçi onaylı gelir; ADR 0168). */
  defaults?: Partial<Values>;
  source: SupplierSource;
  onClose: () => void;
  onSaved: (supplier: Supplier) => void;
}) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`purchasing.suppliers.dialog.${key}`, options);
  const [values, setValues] = useState<Values>(
    supplier ? valuesOf(supplier) : { ...EMPTY, ...defaults },
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (patch: Partial<Values>) => setValues((v) => ({ ...v, ...patch }));
  const regionOptions = useRegionOptions();
  const error = (field: string) =>
    errors[field]
      ? t(`purchasing.field.${errors[field]}`, { defaultValue: errors[field] })
      : undefined;
  const text = (field: keyof Values, label: string, extra: Record<string, unknown> = {}) => (
    <TextInput
      label={label}
      value={values[field] as string}
      onChange={(event) => set({ [field]: event.currentTarget.value } as Partial<Values>)}
      error={error(field)}
      {...extra}
    />
  );

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="xl"
      title={supplier ? k('editTitle', { name: supplier.name }) : k('newTitle')}
      closeButtonProps={{ 'aria-label': k('cancel') }}
    >
      <form
        noValidate
        onSubmit={async (event) => {
          event.preventDefault();
          const parsed = parseSupplier(inputOf(values));
          if (!parsed.ok) {
            const found: Record<string, string> = {};
            for (const issue of parsed.issues) found[issue.path] ??= issue.code;
            setErrors(found);
            return;
          }
          setErrors({});
          setBusy(true);
          setSaveError(null);
          try {
            const input = parsed.value as SupplierInput;
            const saved = supplier
              ? await source.update(supplier.id, supplier.rowVersion, input)
              : await source.create(input);
            onSaved(saved);
          } catch (failure) {
            setSaveError(errorCode(failure));
          } finally {
            setBusy(false);
          }
        }}
      >
        <fieldset className={classes.dialogSection}>
          <legend>{k('general')}</legend>
          <div className={classes.dialogGrid}>
            {text('name', k('name'), { required: true, maxLength: 200, 'data-autofocus': true })}
            <TextInput label={k('code')} value={supplier?.code ?? k('codeAuto')} readOnly />
            <Select
              label={k('type')}
              required
              data={SUPPLIER_TYPES.map((value) => ({
                value,
                label: t(`purchasing.suppliers.types.${value}`),
              }))}
              value={values.supplierType || null}
              onChange={(value) => set({ supplierType: value ?? '' })}
              error={error('supplierType')}
            />
            {text('country', k('country'), { maxLength: 80 })}
            {text('city', k('city'), { maxLength: 80 })}
            {text('address', k('address'), { maxLength: 400 })}
          </div>
          <div className={classes.checks}>
            <Checkbox
              label={k('approved')}
              checked={values.approved}
              onChange={(event) => set({ approved: event.currentTarget.checked })}
            />
            <Checkbox
              label={k('preferred')}
              checked={values.preferred}
              onChange={(event) => set({ preferred: event.currentTarget.checked })}
            />
            <Checkbox
              label={k('emergency')}
              checked={values.emergency}
              onChange={(event) => set({ emergency: event.currentTarget.checked })}
            />
            <Checkbox
              label={k('active')}
              checked={values.active}
              onChange={(event) => set({ active: event.currentTarget.checked })}
            />
          </div>
        </fieldset>
        <fieldset className={classes.dialogSection}>
          <legend>{k('contact')}</legend>
          <div className={classes.dialogGrid}>
            {text('contactPerson', k('contactPerson'), { maxLength: 120 })}
            {text('email', k('email'), { type: 'email', maxLength: 254 })}
            {text('phone', k('phone'), { maxLength: 40 })}
            {text('website', k('website'), { maxLength: 200 })}
          </div>
        </fieldset>
        <fieldset className={classes.dialogSection}>
          <legend>{k('commercial')}</legend>
          <div className={classes.dialogGrid}>
            {text('paymentTerms', k('paymentTerms'), { maxLength: 200 })}
            {text('currency', k('currency'), { maxLength: 3, placeholder: 'USD' })}
            <NumberInput
              label={k('rating')}
              min={1}
              max={5}
              allowDecimal={false}
              value={values.rating}
              onChange={(value) => set({ rating: typeof value === 'number' ? value : '' })}
              error={error('rating')}
            />
            {text('taxInfo', k('taxInfo'), { maxLength: 200 })}
          </div>
          <Textarea
            label={k('bankInfo')}
            autosize
            minRows={2}
            maxLength={1000}
            value={values.bankInfo}
            onChange={(event) => set({ bankInfo: event.currentTarget.value })}
            mt="sm"
          />
          <MultiSelect
            label={t('purchasingMarket.serviceRegions')}
            data={regionOptions}
            value={values.serviceRegions}
            onChange={(serviceRegions) => set({ serviceRegions })}
            error={error('serviceRegions')}
            searchable
            clearable
            mt="sm"
          />
          <Textarea
            label={k('remarks')}
            autosize
            minRows={2}
            maxLength={2000}
            value={values.remarks}
            onChange={(event) => set({ remarks: event.currentTarget.value })}
            mt="sm"
          />
        </fieldset>
        {saveError ? (
          <p className={classes.error} role="alert">
            {t(`purchasing.errors.${saveError}`, { defaultValue: t('purchasing.errors.GENERIC') })}
          </p>
        ) : null}
        <div className={classes.dialogActions}>
          <Button variant="default" onClick={onClose}>
            {k('cancel')}
          </Button>
          <Button type="submit" loading={busy}>
            {k('save')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
